<template>
  <div class="container">
    <div class="inner-container">
      <schedule-info
        :model="model"
        :recipients="recipients"
        @calculate="samplingAllowed => this.$emit('calculate', samplingAllowed)"
        @check="$emit('check')"
      />
      <el-row>
        <el-col class="title">{{
          $t('CampaignForm_Review_CampaignType')
        }}</el-col>
        <el-col class="info">
          {{ campaignType }}
        </el-col>
      </el-row>
      <el-row v-if="model.type !== campaignTypeEnum.TYPE_WORKFLOW">
        <el-col class="title">{{ $t('CampaignForm_Review_Channels') }}</el-col>
        <el-col class="info">{{
          channels ? channels : $t('CampaignForm_Review_Channels_Not_Selected')
        }}</el-col>
      </el-row>
      <el-row>
        <el-col class="title">{{ $t('CampaignForm_Review_Audience') }}</el-col>
        <el-col class="info">
          {{ broadcastLabel }}
        </el-col>
      </el-row>
      <el-row>
        <el-col class="title">{{
          $t('CampaignForm_Review_MultiLanguage')
        }}</el-col>
        <el-col class="info handle-white-spaces">
          <div v-if="model.languages_multi">{{ languages }}</div>
          <div v-else>{{ $t('RestForm_No') }}</div>
        </el-col>
      </el-row>
      <el-row>
        <el-col class="title">{{ $t('CampaignForm_Review_Variants') }}</el-col>
        <el-col class="info">
          <div v-if="model.ab">{{ variants }}</div>
          <div v-else>{{ $t('RestForm_No') }}</div>
        </el-col>
      </el-row>
      <el-row>
        <el-col class="title">{{
          $t('CampaignForm_Review_Goal_Conversion')
        }}</el-col>
        <el-col class="info handle-white-spaces">
          <div v-if="model.conversions == 1">{{ goalConversion }}</div>
          <div v-else>{{ $t('RestForm_No') }}</div>
        </el-col>
      </el-row>
      <el-row v-if="model.voucher_codes_enabled && model.voucher_codes_file">
        <el-col class="title">{{
          $t('CampaignForm_Review_VoucherCodes_Amount')
        }}</el-col>
        <el-col class="info handle-white-spaces">
          <div>{{ model.voucher_codes_file.rows || 0 }}</div>
        </el-col>
      </el-row>
    </div>
    <div class="footer">
      <div class="footer-ct">
        <el-button
          v-if="!readonly && !isTemplateCampaign && (isLive || isPast)"
          type="primary"
          @click="getValidateDialog"
          :disabled="!$can('sendPushCampaign') || !isChannelSelected"
        >
          <i class="ico icon-check"></i>
          {{ updateButtonName }}
        </el-button>
        <el-button
          v-if="!readonly && !isLive && !isPast && !isTemplateCampaign"
          type="primary"
          @click="getValidateDialog"
          :disabled="!$can('sendPushCampaign') || !isChannelSelected"
          data-qa="review-&-launch-btn"
        >
          <i class="ico icon-check"></i>
          {{ launchButtonName }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { channelLabels } from '@/modules/campaign-manage/enum/Labels'
import { languages } from '@/modules/campaign-manage/enum/LanguagesEnum'
import ScheduleInfo from '@/modules/campaign-manage/components/review/ScheduleInfo'
import { EventBus } from '@/core/EventBus'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { broadcastOptions } from '@/modules/campaign-manage/enum/BroadcastOptions'
export default {
  name: 'StepReviewAndLaunch',
  components: { ScheduleInfo },
  props: {
    model: {
      type: Object
    },
    recipients: {
      type: Object
    }
  },
  data() {
    return {
      campaignTypeEnum
    }
  },
  computed: {
    readonly() {
      return (
        this.$route.name === 'ViewCampaign' ||
        this.$route.name === 'ViewTemplateCampaign'
      )
    },
    isLive() {
      return this.model.active === 1
    },
    isPast() {
      return (
        this.model.hasOwnProperty('status_canonical') &&
        this.model.status_canonical === 'past'
      )
    },
    isChannelSelected() {
      let selectedChannels = Object.keys(channelLabels).filter(
        c => this.model[c]
      )

      if (this.model.type === campaignTypeEnum.TYPE_WORKFLOW) {
        return true
      }

      if (this.model.promotion) {
        return true
      }

      return !!selectedChannels.length
    },
    isTemplateCampaign() {
      return (
        this.$route.name === 'CreateTemplateCampaign' ||
        this.$route.name === 'UpdateTemplateCampaign'
      )
    },
    campaignType() {
      let typeList = []
      if (this.model.type === campaignTypeEnum.TYPE_NOTIFICATION) {
        typeList.push(this.$t('Campaign_Dialog_Type_SingleStage'))
      } else if (this.model.type === campaignTypeEnum.TYPE_WORKFLOW) {
        typeList.push(this.$t('Campaign_Dialog_Type_MultiStage'))
      } else if (this.model.type === campaignTypeEnum.TYPE_INAPP) {
        typeList.push(this.$t('Campaign_Dialog_Type_Message'))
      }
      if (
        this.model.type === campaignTypeEnum.TYPE_NOTIFICATION ||
        this.model.type === campaignTypeEnum.TYPE_WORKFLOW
      ) {
        if (this.model.trigger === campaignTriggerTypes.TRIGGER_EVENT) {
          typeList.push(this.$t('Campaign_Dialog_Trigger_Event'))
        } else if (this.model.trigger === campaignTriggerTypes.TRIGGER_API) {
          typeList.push(this.$t('Campaign_Dialog_Trigger_Api'))
        } else if (this.model.trigger === campaignTriggerTypes.TRIGGER_NONE) {
          typeList.push(this.$t('Campaign_Dialog_Type_TimeBased'))
        } else if (
          this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE
        ) {
          typeList.push(this.$t('Campaign_Dialog_Type_Optimove'))
        } else if (
          this.model.trigger === campaignTriggerTypes.TRIGGER_SALESFORCE
        ) {
          typeList.push(this.$t('Campaign_Dialog_Type_Salesforce'))
        }
      }
      return typeList.join(', ')
    },
    channels() {
      let channels = []
      if (this.model.promotion) {
        channels.push(this.$t('CampaignForm_ChannelsSelect_Promotion'))
      }
      Object.keys(channelLabels).map(channelKey => {
        if (this.model[channelKey]) {
          channels.push(this.$t(channelLabels[channelKey]))
        }
      })
      return channels.join(', ')
    },
    languages() {
      let langs = []
      this.model.languages_list.map(lang => {
        if (lang) langs.push(`${languages[lang]} (${lang.toUpperCase()})`)
      })
      return langs.join('\n')
    },
    variants() {
      let variants = []
      this.model.variants.map(variant => {
        if (variant.active) {
          variants.push(
            `${variant.key} ${variant.title ? ` (${variant.title})` : ''}`
          )
        }
      })
      return variants.join(', ')
    },
    goalConversion() {
      let goals = []

      Object.values(this.model.goal_conditions[0]).map(goalObj => {
        let goal = []
        try {
          if (goalObj[0] === 'tags') {
            goal.push(this.$t('CampaignForm_Review_Goal_Conversion_Tag'))
          } else if (goalObj[0] === 'events') {
            goal.push(this.$t('CampaignForm_Review_Goal_Conversion_Events'))
          } else if (goalObj[0] === 'events_value') {
            goal.push(
              this.$t('CampaignForm_Review_Goal_Conversion_Events_Value')
            )
          }
          if (goalObj[2]) {
            goal.push(goalObj[2])
          }
          if (goalObj[4][0] === 'date') {
            switch (goalObj[4][1]) {
              case 'within':
                goal.push(
                  this.$t('CampaignForm_Review_Goal_Conversion_Date_Within')
                )
                goal.push(goalObj[4][2][0])
                goal.push(goalObj[4][2][1])
                break
              case 'between':
                goal.push(
                  this.$t('CampaignForm_Review_Goal_Conversion_Date_Between')
                )
                goal.push(goalObj[4][2].replace('to', '/'))
                break
            }
          }
          goals.push(goal.join(', '))
        } catch (e) {}
      })
      return goals.join('\n')
    },
    launchButtonName() {
      if (this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE) {
        return this.$t('CampaignStepped_Btn_SaveOptimoveTemplate')
      }

      if (this.isTemplateCampaign) {
        return this.$t('CampaignStepped_Btn_SaveTemplate')
      }

      return this.$t('CampaignStepped_Btn_ReviewAndLaunch')
    },
    updateButtonName() {
      if (this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE) {
        return this.$t('CampaignStepped_Btn_SaveOptimoveTemplate')
      }

      if (this.isTemplateCampaign) {
        return this.$t('CampaignStepped_Btn_SaveTemplate')
      }

      return this.$t('CampaignStepped_Btn_ReviewAndUpdate')
    },
    broadcastLabel() {
      if (this.model.broadcast === broadcastOptions.CUSTOM_SEGMENT) {
        return this.$t('CampaignForm_Review_Audience_Custom_Segment')
      } else if (this.model.broadcast === broadcastOptions.EVERYONE_SEGMENT) {
        return this.$t('CampaignForm_Review_Audience_Send_To_All')
      } else if (this.model.broadcast === broadcastOptions.ROLLING_SEGMENT) {
        return this.$t('CampaignForm_Review_Audience_Rolling_Segment')
      }
    }
  },
  methods: {
    getValidateDialog() {
      EventBus.$emit('campaignValidateDialog', 0)
    }
  }
}
</script>

<style scoped lang="scss">
.inner-container {
  padding: 0 20% 60px 20%;
}
.title {
  font-weight: 600;
  font-size: 14px;
  width: 40%;
  color: #4b515f;
  font-family: Open Sans, sans-serif;
}
.info {
  font-size: 14px;
  width: 60%;
  color: #7e8596;
  font-weight: 400;
}
>>> .el-row {
  padding: 10px 0;
  line-height: 22px;
}
.footer {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}
.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
.handle-white-spaces {
  white-space: pre;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/pages/StepReviewAndLaunch.vue